import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Clock, Calendar, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/Eyebrow";
import { SafeImage } from "@/components/SafeImage";
import { ProductCard } from "@/components/ProductCard";
import { ProductChip } from "@/components/ProductChip";
import { useProductModal } from "@/context/ProductModalContext";
import {
  RECIPE_CATEGORY_LABELS,
  formatRecipeDate,
} from "@/data/recipes";
import { getPublicRecipeBySlug } from "@/lib/public.functions";
import { useRecipesHidden, useProductsHidden } from "@/hooks/useSiteSettings";
import { assertSectionVisible } from "@/lib/visibility";

export const Route = createFileRoute("/ricette/$slug")({
  beforeLoad: () => assertSectionVisible("hide_recipes"),
  loader: async ({ params }) => {
    const { recipe, linkedProducts, related } = await getPublicRecipeBySlug({ slug: params.slug });
    if (!recipe) throw notFound();
    return { recipe, linkedProducts, related };
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: `${loaderData.recipe.title} — Ricette dalla bottega` },
          { name: "description", content: loaderData.recipe.excerpt },
          { property: "og:title", content: loaderData.recipe.title },
          { property: "og:description", content: loaderData.recipe.excerpt },
          { property: "og:type", content: "article" },
          { property: "og:image", content: loaderData.recipe.cover },
          { property: "og:url", content: `https://labottegadellecarnidamarco.it/ricette/${loaderData.recipe.slug}` },
        ]
      : [],
    links: loaderData
      ? [{ rel: "canonical", href: `https://labottegadellecarnidamarco.it/ricette/${loaderData.recipe.slug}` }]
      : [],
    scripts: loaderData
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Recipe",
              name: loaderData.recipe.title,
              description: loaderData.recipe.excerpt,
              image: loaderData.recipe.cover,
              datePublished: loaderData.recipe.date,
              recipeCategory: RECIPE_CATEGORY_LABELS[loaderData.recipe.category],
              recipeIngredient: loaderData.recipe.ingredients.map(
                (i) => `${i.qty} ${i.item}`
              ),
              recipeInstructions: loaderData.recipe.steps.map((s, idx) => ({
                "@type": "HowToStep",
                position: idx + 1,
                text: s,
              })),
            }),
          },
        ]
      : [],
  }),
  notFoundComponent: () => (
    <div className="min-h-[50vh] flex items-center justify-center pt-20">
      <div className="text-center">
        <h1 className="font-display text-4xl">Ricetta non trovata</h1>
        <Link to="/ricette" className="mt-4 inline-block text-brand-red">
          Torna alle ricette
        </Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-20 text-center">{error instanceof Error ? error.message : String(error)}</div>
  ),
  component: RecipePage,
});

function RecipePage() {
  const recipesHidden = useRecipesHidden();
  const productsHidden = useProductsHidden();
  const navigate = useNavigate();
  useEffect(() => {
    if (recipesHidden) navigate({ to: "/", replace: true });
  }, [recipesHidden, navigate]);
  if (recipesHidden) return null;
  const { recipe, linkedProducts, related } = Route.useLoaderData() as {
    recipe: import("@/data/recipes").Recipe;
    linkedProducts: import("@/data/products").Product[];
    related: import("@/data/recipes").Recipe[];
  };
  const { openProductModal } = useProductModal();
  const visibleLinkedProducts = productsHidden ? [] : linkedProducts;

  return (
    <>
      {/* BREADCRUMB */}
      <div className="container mx-auto px-4 pt-14 pb-6">
        <motion.nav
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-xs text-brand-warm-gray flex items-center gap-1 flex-wrap"
        >
          <Link to="/" className="hover:text-brand-red transition-colors">
            Home
          </Link>
          <ChevronRight size={12} />
          <Link to="/ricette" className="hover:text-brand-red transition-colors">
            Ricette
          </Link>
          <ChevronRight size={12} />
          <span>{RECIPE_CATEGORY_LABELS[recipe.category]}</span>
          <ChevronRight size={12} />
          <span className="text-brand-charcoal">{recipe.title}</span>
        </motion.nav>
      </div>

      {/* HEADER ARTICOLO */}
      <section className="container mx-auto px-4 pb-10">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center">
            <Eyebrow>{RECIPE_CATEGORY_LABELS[recipe.category]}</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 leading-tight text-brand-charcoal">
              {recipe.title}
            </h1>
            <div className="flex items-center justify-center gap-5 mt-5 text-xs uppercase tracking-[0.16em] text-brand-warm-gray">
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={13} /> {formatRecipeDate(recipe.date)}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={13} /> {recipe.readingTime} min di lettura
              </span>
            </div>
            <p className="mt-6 text-lg md:text-xl font-italic-serif italic text-brand-warm-gray leading-relaxed">
              {recipe.excerpt}
            </p>
          </div>
        </Reveal>
      </section>

      {/* COVER */}
      <section className="container mx-auto px-4 pb-12">
        <Reveal y={20} duration={0.9}>
          <div className="aspect-[16/9] overflow-hidden rounded-md bg-brand-cream-dark shadow-[var(--shadow-soft)]">
            <SafeImage
              src={recipe.cover}
              alt={recipe.title}
              className="w-full h-full object-cover"
            />
          </div>
        </Reveal>
      </section>

      {/* CORPO */}
      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="text-base md:text-lg text-brand-charcoal/85 leading-[1.85]">
              {recipe.intro}
            </p>
          </Reveal>

          {/* CHIP PRODOTTO INLINE */}
          {recipe.productSlugs[0] && (
            <Reveal>
              <div className="mt-6">
                <p className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray text-center mb-2">
                  Per questa ricetta usiamo
                </p>
                <ProductChip slug={recipe.productSlugs[0]} />
              </div>
            </Reveal>
          )}

          {/* Ingredienti */}
          <Reveal>
            <div className="mt-12">
              <h2 className="font-display text-3xl text-brand-charcoal">Ingredienti</h2>
              <div className="gold-divider mt-3 max-w-[60px]" />
              <ul className="mt-6 divide-y divide-brand-charcoal/10">
                {recipe.ingredients.map((ing, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="flex items-baseline gap-4 py-3"
                  >
                    <span className="font-numeric font-semibold text-brand-red min-w-[80px]">
                      {ing.qty}
                    </span>
                    <span className="text-brand-charcoal">{ing.item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* Procedimento */}
          <Reveal>
            <div className="mt-12">
              <h2 className="font-display text-3xl text-brand-charcoal">Procedimento</h2>
              <div className="gold-divider mt-3 max-w-[60px]" />
              <ol className="mt-6 space-y-6">
                {recipe.steps.map((s, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className="flex gap-5"
                  >
                    <span className="font-display text-2xl text-brand-red shrink-0 w-10 leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-brand-charcoal/85 leading-[1.85]">{s}</p>
                  </motion.li>
                ))}
              </ol>
            </div>
          </Reveal>

          {recipe.tips && (
            <Reveal>
              <div className="mt-12 bg-brand-cream-dark/40 border-l-4 border-brand-gold rounded-md p-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-warm-gray mb-2">
                  Consiglio del macellaio
                </div>
                <p className="font-italic-serif italic text-brand-charcoal/90 leading-relaxed text-lg">
                  {recipe.tips}
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* PRODOTTI COLLEGATI */}
      {visibleLinkedProducts.length > 0 && (
        <section className="bg-brand-cream-dark/30 py-16 border-y border-brand-gold/20">
          <div className="container mx-auto px-4">
            <Reveal>
              <div className="text-center mb-2">
                <Eyebrow>Dalla bottega</Eyebrow>
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-brand-charcoal text-center">
                Usati in questa ricetta
              </h2>
              <p className="text-center font-italic-serif italic text-brand-warm-gray mt-3">
                Tutti i prodotti li trovi al banco o prenotabili online.
              </p>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mt-10">
              {visibleLinkedProducts.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.06}>
                  <ProductCard
                    product={p}
                    onClick={(slug) => openProductModal(slug)}
                  />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CORRELATE */}
      {related.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <Reveal>
              <h2 className="font-display text-3xl text-brand-charcoal mb-8">
                Continua a leggere
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.08}>
                  <Link
                    to="/ricette/$slug"
                    params={{ slug: r.slug }}
                    className="group block bg-white rounded-md overflow-hidden lift-on-hover zoom-img-parent shadow-[var(--shadow-soft)] h-full"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-brand-cream-dark">
                      <SafeImage
                        src={r.cover}
                        alt={r.title}
                        className="zoom-img w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-5">
                      <div className="text-[11px] uppercase tracking-[0.16em] text-brand-red">
                        {RECIPE_CATEGORY_LABELS[r.category]}
                      </div>
                      <h3 className="font-display text-xl text-brand-charcoal mt-2 leading-tight group-hover:text-brand-red transition-colors">
                        {r.title}
                      </h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link
                to="/ricette"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-brand-charcoal hover:text-brand-red transition-colors"
              >
                <ArrowLeft size={14} /> Tutte le ricette
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
}